import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import {Image} from 'primereact/image'
import {InputTextarea} from 'primereact/inputtextarea'
import {Sidebar} from 'primereact/sidebar'
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react'
import {createOrUpdateGenericInfo} from '../../api/ServiceWorker'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import FormHeader from '../form/FormHeader'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {refetchEvent} from '../table-utils/TableUtils'
import {useSymptomsPathwayByUser} from '../user-property/query-keys/UserPropertyQueryManager'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import PatientMedicalHistory from './PatientMedicalHistory'

const PATH = 'userSymptom'
const QueryKey = 'User-Symptom'
const SymptomQueryKey = 'Symptom-By-Pathway'

const PatientSymptomForm = forwardRef(({onChange = () => {}}, ref) => {
    useImperativeHandle(ref, () => ({
        showForm: () => showForm(),
        hideForm: () => hideForm(),
    }))
    const queryClient = useQueryClient()
    const {translateCommons} = TranslateUtils()
    const {user} = useAuth()
    const [selected, setSelected] = useState([])

    const {data: symptomData, refetch} = useSymptomsPathwayByUser(
        user?.id,
        user?.id,
        !!user?.id,
    )

    const symptomOptions = useMemo(() => {
        if (
            !symptomData ||
            !Array.isArray(symptomData) ||
            symptomData?.length < 1
        ) {
            return []
        }

        let options = []
        for (let i = 0; i < symptomData?.length; i++) {
            let item = {
                label: translateCommons(symptomData[i]),
                value: symptomData[i],
            }
            options.push(item)
        }
        return options
    }, [symptomData, translateCommons])

    const [visible, setVisible] = useState(false)
    const [visible2, setVisible2] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)

    const [symptom, setSymptom] = useState('')
    const [additionalDetails, setAdditionalDetails] = useState([''])

    const addDetailField = () => {
        setAdditionalDetails(prev => [...prev, ''])
    }

    const removeDetailField = index => {
        if (additionalDetails.length > 1) {
            setAdditionalDetails(prev => prev.filter((_, i) => i !== index))
        }
    }

    const updateDetailField = (index, value) => {
        setAdditionalDetails(prev =>
            prev.map((item, i) => (i === index ? value : item)),
        )
    }

    const showForm = () => {
        blockOverflownScroll()
        setVisible(true)
    }

    const hideForm = () => {
        unblockOverflownScroll()
        setSelected([])
        setSymptom('')
        setVisible(false)
    }

    const {mutate: handleCreateOrUpdate} = useMutation(
        data => createOrUpdateGenericInfo(PATH, user?.id, data),
        {
            onSuccess: data => {
                if (!!data?.id) {
                    const {userPropertyKeys} = QueryKeyManager(
                        QueryKeyTypes.userProperty,
                        user?.id,
                    )

                    queryClient.invalidateQueries(
                        userPropertyKeys.pattern({
                            type: UserPropertyType.userSymptom,
                        }),
                    )

                    setVisible2(true)
                    setSymptom('')
                    setAdditionalDetails([''])
                    setSelected([])
                    showNotification(
                        severityNotification.success,
                        'Symptom reported !!',
                    )
                }
            },
        },
    )

    const getSelectValue = (select, additionalDetailsList) => {
        let str = ''
        if (select && Array.isArray(select) && select.length > 0) {
            str = select.join(', ')
        }

        // Filter out empty additional details
        const validDetails = additionalDetailsList.filter(
            detail => detail.trim() !== '',
        )
        if (validDetails.length > 0) {
            const detailsStr = validDetails
                .map(detail => detail.charAt(0).toUpperCase() + detail.slice(1))
                .join(', ')

            if (str) {
                str = str + ', ' + detailsStr
            } else {
                str = detailsStr
            }
        }

        return str
    }

    const handleSubmit = useCallback(() => {
        let toPostData = {
            symptom: getSelectValue(selected, additionalDetails),
            userId: user?.id ? user?.id : null,
        }

        handleCreateOrUpdate(toPostData)
        setSelected([])
        setSymptom('')
        setAdditionalDetails([''])
        setIsSubmitted(!isSubmitted)
    }, [handleCreateOrUpdate, additionalDetails, user?.id, selected])

    const handleOnSelect = e => {
        setSelected(prev => {
            if (prev.includes(e)) {
                return prev.filter(item => item !== e)
            } else {
                return [...prev, e]
            }
        })
    }

    useEffect(() => {
        if (!!SymptomQueryKey && !!user?.id) {
            refetchEvent.on(
                'REFETCH-DATA-TABLE-' + SymptomQueryKey + user?.id,
                () => {
                    refetch()
                },
            )

            return () => {
                refetchEvent.removeListener(
                    'REFETCH-DATA-TABLE-' + SymptomQueryKey + user?.id,
                )
            }
        }
    }, [refetch, user?.id])

    return (
        <Sidebar
            className="p-sidebar-full"
            visible={visible}
            blockScroll
            style={{background: '#f8fafc'}}
            onHide={() => hideForm()}
            fullScreen>
            <div className="min-h-screen">
                <FormHeader
                    header={translateCommons('SYMPTOM FORM')}
                    onHide={() => hideForm()}
                />

                <div className="flex justify-content-center align-items-start p-4">
                    <div className="w-full max-w-screen-xl">
                        <div className="grid">
                            {/* Main Content */}
                            <div className="col-12 lg:col-8">
                                <div className="surface-card border-round-lg shadow-3 p-6">
                                    {/* Header Section */}
                                    <div className="text-center mb-6">
                                        <div className="mb-4">
                                            <Image
                                                height="180px"
                                                src={require('./../../assets/images/Observation.jpg')}
                                                className="border-round"
                                            />
                                        </div>
                                        <h1 className="text-5xl font-bold text-700 mb-3 line-height-3">
                                            {translateCommons(
                                                'Report your symptoms',
                                            )}
                                        </h1>
                                        <div className="max-w-40rem mx-auto text-600 line-height-3">
                                            <p className="mb-3">
                                                {translateCommons(
                                                    "You can either choose from a selection using 'Choose your symptom' button down below or type what you're feeing in the textbox.",
                                                )}
                                            </p>
                                            <p className="mb-0">
                                                {translateCommons(
                                                    "Click 'Submit' when you are done with your choice.",
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Symptoms Selection */}
                                    <div className="mb-5">
                                        <h3 className="text-2xl font-semibold text-700 mb-4">
                                            {translateCommons(
                                                'Choose your symptom',
                                            )}
                                            {selected.length > 0 && (
                                                <span className="text-sm font-normal">
                                                    ( {selected.length} options
                                                    selected)
                                                </span>
                                            )}
                                        </h3>

                                        {symptomOptions.length > 0 ? (
                                            <div className="grid">
                                                {symptomOptions.map(
                                                    (item, index) => (
                                                        <div
                                                            key={index}
                                                            className="col-12 sm:col-6 lg:col-4 mb-1">
                                                            <div
                                                                className={`
                                                                border-1 border-round-lg p-3 cursor-pointer transition-all transition-duration-200
                                                                ${
                                                                    selected.includes(
                                                                        item.value,
                                                                    )
                                                                        ? 'border-0 bg-gray-100 text-700 '
                                                                        : 'border-100 hover:border-400 hover:bg-gray-50 border-[0.5px]'
                                                                }
                                                            `}
                                                                onClick={() =>
                                                                    handleOnSelect(
                                                                        item.value,
                                                                    )
                                                                }>
                                                                <div className="flex align-items-center">
                                                                    <i
                                                                        className={`
                                                                    mr-3 text-lg
                                                                    ${
                                                                        selected.includes(
                                                                            item.value,
                                                                        )
                                                                            ? 'pi pi-check-circle text-900 text-teal-700'
                                                                            : 'pi pi-circle text-400'
                                                                    }
                                                                `}></i>
                                                                    <span className="font-medium">
                                                                        {
                                                                            item.label
                                                                        }
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center p-4 bg-gray-50 border-round">
                                                <i className="pi pi-info-circle text-400 text-2xl mb-3"></i>
                                                <p className="text-600 m-0">
                                                    No symptoms available
                                                </p>
                                            </div>
                                        )}

                                        <div className="mt-4 p-3 bg-gray-50 border-left-3 border-teal-600">
                                            <p className="text-sm text-gray-700 m-0">
                                                <i className="pi pi-info-circle mr-2"></i>
                                                {translateCommons(
                                                    'Note: You can either choose from the suggestion list or manually enter your symptoms',
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <Divider align="center" className="mb-5">
                                        <span className="bg-surface-0 text-600 font-semibold px-3">
                                            {translateCommons('And')}
                                        </span>
                                    </Divider>

                                    {/* Additional Details List */}
                                    <div className="mb-5">
                                        <div className="flex align-items-center justify-content-between mb-3">
                                            <h3 className="text-2xl font-semibold text-700 m-0">
                                                {translateCommons(
                                                    'Additional ',
                                                )}
                                            </h3>
                                            <Button
                                                icon="pi pi-plus"
                                                label={translateCommons(
                                                    'Add more',
                                                )}
                                                className="p-button-outlined p-button-sm focus:shadow-none border-none"
                                                style={{
                                                    borderColor: '#6b7280',
                                                    color: '#6b7280',
                                                }}
                                                onClick={addDetailField}
                                                type="button"
                                            />
                                        </div>

                                        <div className="flex flex-column gap-3">
                                            {additionalDetails.map(
                                                (detail, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex align-items-center gap-2">
                                                        <div className="flex-1">
                                                            <InputTextarea
                                                                placeholder={translateCommons(
                                                                    `
                                                                    `,
                                                                )}
                                                                className="w-full bg-white border border-gray-100 rounded-md  text-xl
                                                                 focus:outline-none focus:ring-0 focus:shadow-none"
                                                                value={detail}
                                                                onChange={e =>
                                                                    updateDetailField(
                                                                        index,
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                rows={4}
                                                                style={{
                                                                    resize: 'vertical',
                                                                }}
                                                            />
                                                        </div>
                                                        {additionalDetails.length >
                                                            1 && (
                                                            <Button
                                                                icon="pi pi-trash"
                                                                className="p-button-danger p-button-outlined p-button-sm flex-shrink-0"
                                                                onClick={() =>
                                                                    removeDetailField(
                                                                        index,
                                                                    )
                                                                }
                                                                type="button"
                                                                style={{
                                                                    height: 'fit-content',
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                ),
                                            )}
                                        </div>

                                        <div className="mt-3 p-3 bg-gray-50 border-left-3 border-teal-600">
                                            <p className="text-sm text-gray-700 m-0">
                                                <i className="pi pi-lightbulb mr-2"></i>
                                                {translateCommons(
                                                    'Note: You can add multiple detailed descriptions about your health condition. Each field represents a separate symptom or condition detail.',
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex justify-content-center gap-3 w-full">
                                        <Button
                                            label={translateCommons('Cancel')}
                                            icon="pi pi-times"
                                            className="p-button-outlined px-6 py-3 text-lg font-semibold w-full border-1 border-gray-300"
                                            style={{
                                                borderColor: '#6b7280',
                                                color: '#6b7280',
                                            }}
                                            onClick={hideForm}
                                            type="button"
                                        />
                                        <Button
                                            label={translateCommons('Submit')}
                                            icon="pi pi-send"
                                            className="px-6 py-3 text-lg font-semibold w-full"
                                            style={{
                                                backgroundColor: '#14b8a6',
                                                borderColor: '#14b8a6',
                                            }}
                                            onClick={handleSubmit}
                                            disabled={
                                                selected.length === 0 &&
                                                additionalDetails.every(
                                                    detail => !detail.trim(),
                                                )
                                            }
                                            type="button"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Medical History Sidebar */}
                            <div className="col-12 lg:col-4">
                                <div
                                    className="surface-card border-round-lg shadow-3 p-4 sticky"
                                    style={{top: '2rem'}}>
                                    <PatientMedicalHistory userId={user?.id} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Success Dialog */}
                <Dialog
                    visible={visible2}
                    onHide={() => setVisible2(false)}
                    modal
                    breakpoints={{'960px': '75vw', '640px': '100vw'}}
                    style={{width: '40vw'}}
                    closable={false}
                    showHeader={false}
                    className="border-round-lg"
                    footer={
                        <div className="flex justify-content-center pt-4">
                            <Button
                                icon="pi pi-check"
                                onClick={() => setVisible2(false)}
                                label={translateCommons('Close')}
                                className="px-6 py-2 font-semibold"
                                style={{
                                    backgroundColor: '#14b8a6',
                                    borderColor: '#14b8a6',
                                }}
                            />
                        </div>
                    }>
                    <div className="text-center py-6">
                        <div
                            className="inline-flex align-items-center justify-content-center bg-teal-100 text-teal-800 border-circle mb-4"
                            style={{width: '80px', height: '80px'}}>
                            <i className="pi pi-check text-4xl"></i>
                        </div>
                        <h2 className="text-2xl font-bold text-teal-700 mb-3">
                            {translateCommons('Submit Successfully')}
                        </h2>
                        <p className="text-600 line-height-3 mb-0">
                            Your symptoms have been reported successfully. Thank
                            you for providing this information.
                        </p>
                    </div>
                </Dialog>
            </div>
        </Sidebar>
    )
})

export default PatientSymptomForm
